import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  INSIGHT_PATTERNS, 
  getStrategicPriorities, 
  generate90DayPlan,
  mockEngineInput 
} from '../data/insightEngineLogic';

export const InsightEngine = () => {
  const [metrics, setMetrics] = useState(mockEngineInput);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const runEngine = () => {
    setIsProcessing(true);
    setResults(null);
    setTimeout(() => {
      const patterns = INSIGHT_PATTERNS.filter(p => p.condition(metrics));
      const priorities = getStrategicPriorities(patterns);
      const plan = generate90DayPlan(patterns);
      setResults({ patterns, priorities, plan });
      setIsProcessing(false);
    }, 2000);
  };

  const dashboardMetrics = [
    { id: 'we', label: 'WE Score', color: 'indigo' },
    { id: 'orgHealth', label: 'Org Health', color: 'emerald' },
    { id: 'culture', label: 'Culture Profile', color: 'violet' },
    { id: 'leadership', label: 'Leadership Profile', color: 'blue' },
    { id: 'conflict', label: 'Conflict Risk', color: 'rose' },
    { id: 'stress', label: 'Stress Index', color: 'orange' },
    { id: 'safety', label: 'Psych Safety', color: 'cyan' }
  ];

  const radarOption = {
    radar: {
      indicator: dashboardMetrics.map(m => ({ name: m.label, max: 100 })),
      shape: 'circle',
      name: { textStyle: { color: '#64748b', fontSize: 10, fontWeight: 'bold' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: dashboardMetrics.map(m => metrics[m.id]),
        name: 'Current State',
        itemStyle: { color: '#6366f1' },
        areaStyle: { opacity: 0.1 }
      }]
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Insight Engine</h1>
          <p className="text-slate-500 mt-1">Avansert korrelasjonsanalyse for å finne rotårsaker.</p>
        </div>
        <Button 
          variant="primary" 
          onClick={runEngine} 
          disabled={isProcessing}
          icon={<SafeIcon name="Zap" />}
          className="shadow-xl shadow-indigo-100"
        >
          {isProcessing ? 'Analyserer Korrelasjoner...' : 'Kjør Insight Engine'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Tuning */}
        <Card className="lg:col-span-1 border-none shadow-sm">
          <CardHeader title="Input Dimensjoner" subtitle="Data hentet fra aktive indekser" />
          <CardContent className="space-y-5">
            {dashboardMetrics.map(m => (
              <div key={m.id} className="space-y-2">
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span>{m.label}</span>
                  <span className="text-slate-900">{metrics[m.id]}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" max="100" 
                  value={metrics[m.id]} 
                  onChange={(e) => setMetrics({...metrics, [m.id]: parseInt(e.target.value)})}
                  className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Visual & Processing */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-slate-900 border-none h-full min-h-[400px] flex flex-col items-center justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] -mr-32 -mt-32" />
            <AnimatePresence mode="wait">
              {isProcessing ? (
                <motion.div 
                  key="loader"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-center space-y-6 relative z-10"
                >
                  <div className="relative">
                    <div className="w-24 h-24 border-4 border-white/5 rounded-full" />
                    <div className="w-24 h-24 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin absolute top-0" />
                    <SafeIcon name="Cpu" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl text-indigo-400" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-white font-bold text-lg">Kobler sammen datasett...</p>
                    <p className="text-slate-400 text-xs uppercase tracking-widest">Sjekker 4 systemiske mønstre</p>
                  </div>
                </motion.div>
              ) : !results ? (
                <motion.div key="empty" className="text-center space-y-6">
                  <div className="h-64 w-64 md:w-80">
                    <ReactECharts option={radarOption} style={{ height: '100%' }} />
                  </div>
                  <p className="text-slate-500 text-sm italic">Klikk på knappen over for å starte dyp-analysen.</p>
                </motion.div>
              ) : (
                <motion.div 
                  key="results"
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="w-full p-10 space-y-8 relative z-10"
                >
                   <div className="flex items-center gap-4 text-white border-b border-white/10 pb-6">
                     <div className="w-16 h-16 rounded-2xl bg-indigo-500 flex items-center justify-center shadow-2xl">
                       <SafeIcon name="Award" className="text-3xl" />
                     </div>
                     <div>
                       <h2 className="text-2xl font-black uppercase tracking-tight">Executive Insight Summary</h2>
                       <p className="text-indigo-300 text-sm">Basert på {results.patterns.length} identifiserte systemiske avvik.</p>
                     </div>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {results.patterns.map(p => (
                       <div key={p.id} className="p-5 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-colors">
                         <div className="flex justify-between items-start mb-3">
                           <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${p.severity === 'Critical' ? 'bg-rose-500 text-white' : 'bg-orange-500 text-white'}`}>
                             {p.severity}
                           </span>
                           <span className="text-[10px] font-bold text-slate-500">{p.type}</span>
                         </div>
                         <h4 className="font-bold text-white mb-2">{p.title}</h4>
                         <p className="text-xs text-slate-400 leading-relaxed">{p.hypothesis}</p>
                       </div>
                     ))}
                   </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      </div>

      {results && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest ml-1">Strategic Intervention Priorities</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {results.priorities.map((p, i) => (
                <Card key={i} className="border-none shadow-xl bg-white group hover:translate-y-[-4px] transition-all">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      <SafeIcon name="Target" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{p.title}</h4>
                      <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2">{p.focus} fokus</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{p.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-l-4 border-l-slate-900 shadow-xl border-none">
              <CardHeader title="Recommended 90-Day Action Plan" icon={<SafeIcon name="Calendar" />} />
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {results.plan.map((item, i) => (
                    <div key={i} className="p-6 flex gap-8 items-start hover:bg-slate-50 transition-colors">
                      <div className="w-24 shrink-0">
                        <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{item.day}</p>
                      </div>
                      <div className="space-y-1">
                        <h5 className="font-bold text-slate-900">{item.task}</h5>
                        <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="bg-emerald-600 text-white border-none p-2 shadow-xl shadow-emerald-100">
              <CardContent className="p-8 space-y-6">
                <SafeIcon name="PieChart" className="text-4xl text-emerald-200" />
                <h3 className="text-xl font-black uppercase tracking-tight">Root Cause Hypothesis</h3>
                <p className="text-xs text-emerald-50 leading-relaxed italic">
                  "Dataen tyder på at deres nåværende konfliktnivå ikke er relasjonelt, men strukturelt. Det er manglende rolleklarhet i lederleddet som skaper friksjonen i de operative teamene."
                </p>
                <div className="pt-4 border-t border-white/20">
                  <p className="text-[10px] font-bold text-emerald-200 uppercase mb-2">Konfidensnivå</p>
                  <div className="flex justify-between items-end">
                    <span className="text-2xl font-black">84%</span>
                    <span className="text-[10px] uppercase">AI Verifisert</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-50 border-slate-200">
              <CardHeader title="Ekspert-validering" icon={<SafeIcon name="User" />} />
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Denne analysen er klar for gjennomgang med en senior organisasjonspsykolog for å sikre kontekstuell forståelse før tiltak iverksettes.
                </p>
                <Button variant="secondary" className="w-full text-[10px] font-black uppercase py-4">Book Gjennomgang</Button>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      )}
    </div>
  );
};